import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Trash2, Plus, Mail, Check, AlertTriangle, Pencil, X, Copy } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabase'
import { useClients, clientName, type Client } from '@/hooks/use-clients'
import { AddressPicker, composeAddress, type AddressValue } from '@/components/address-picker'
import { useJobs } from '@/hooks/use-jobs'
import { useTeam } from '@/hooks/use-company'
import { useAuth } from '@/hooks/use-auth'
import { ClientAssetsCard } from '@/components/client-assets-card'
import { Page, PageHeader, Card, CardHeader, Table, Trow, StatusPill, Pagination, SkeletonRows, EmptyState, btnPrimary, btnSecondary, btnGhost, hoverActions } from '@/components/rm'
import { cn } from '@/lib/utils'
import { MessageThread } from '@/components/message-thread'
import { ClientDocuments } from '@/components/client-documents'
import { Input, Textarea, Modal, Field } from '@/components/rm/form'
import { phFormat, displayPhMobile } from '@/lib/ph'

interface EmailRow { id: number; subject: string | null; to_addresses: string | null; success: boolean | null; created_at: string | null; body: string | null; mailer_response: string | null }
interface NoteRow { id: string; body: string | null; type: string | null; created_at: string | null }

export const Route = createFileRoute('/_app/clients_/$id')({ component: ClientDetailPage })

const PAGE = 8
const JOB_COLS = '1fr 150px 170px 120px'
function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="flex items-start gap-4 py-2 text-[13.5px]">
      <span className="w-28 flex-none text-token-muted">{k}</span>
      <span className="font-medium text-token-primary">{v || '—'}</span>
    </div>
  )
}
function fmt(iso: string | null) {
  if (!iso) return ''
  return phFormat(new Date(iso), 'datetime', { day: 'numeric', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' })
}

function ClientDetailPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { client, isLoadingDetail, update, remove } = useClients(id)
  const { duplicate } = useJobs()
  const { members } = useTeam()
  const dupJob = async (jobId: number) => {
    try { const r = await duplicate.mutateAsync(jobId); toast.success('Job duplicated'); navigate({ to: '/jobs/$id/edit', params: { id: String(r.id) } }) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const { companyId, user } = useAuth()
  const userName = (uid: string | null) => { if (!uid) return '—'; const u = members.find((m) => m.id === uid); return u ? [u.first_name, u.last_name].filter(Boolean).join(' ') || u.email || '—' : '—' }

  // ── Job history (server-side paginated + searched) ──
  const [jobSearch, setJobSearch] = useState('')
  const [jobPage, setJobPage] = useState(0)
  const jobs = useQuery({
    queryKey: ['client-jobs', id, jobPage, jobSearch],
    queryFn: async () => {
      let q = supabase.from('task')
        .select('id, name, status, user_id, address, planned_start_date', { count: 'exact' })
        .eq('contact_id', id).is('deleted_at', null).eq('is_draft', false)
      if (jobSearch.trim()) q = q.or(`name.ilike.%${jobSearch}%,address.ilike.%${jobSearch}%`)
      const { data, error, count } = await q
        .order('planned_start_date', { ascending: false, nullsFirst: false })
        .range(jobPage * PAGE, jobPage * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as { id: number; name: string | null; status: string | null; user_id: string | null; address: string | null; planned_start_date: string | null }[], count: count ?? 0 }
    },
  })

  // ── Stable task-id list for this client (drives communications) ──
  const taskIds = useQuery({
    queryKey: ['client-taskids', id],
    queryFn: async (): Promise<number[]> => {
      const { data, error } = await supabase.from('task').select('id').eq('contact_id', id).eq('is_draft', false).is('deleted_at', null).limit(2000)
      if (error) throw error
      return (data ?? []).map((r) => (r as { id: number }).id)
    },
  })

  // ── Communications (server-side paginated) ──
  const [commPage, setCommPage] = useState(0)
  const [openEmail, setOpenEmail] = useState<EmailRow | null>(null)
  const emails = useQuery({
    queryKey: ['client-emails', id, commPage, taskIds.data?.length],
    enabled: taskIds.data !== undefined,
    queryFn: async () => {
      const ids = taskIds.data ?? []
      if (!ids.length) return { rows: [] as EmailRow[], count: 0 }
      const { data, error, count } = await supabase.from('notification_email_history')
        .select('id, subject, to_addresses, success, created_at, body, mailer_response', { count: 'exact' })
        .in('task_id', ids).order('created_at', { ascending: false })
        .range(commPage * PAGE, commPage * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as EmailRow[], count: count ?? 0 }
    },
  })

  // ── Notes (server-side paginated + add/edit/delete) ──
  const [notePage, setNotePage] = useState(0)
  const [newNote, setNewNote] = useState('')
  const [editing, setEditing] = useState<NoteRow | null>(null)
  const [editText, setEditText] = useState('')
  const [editClient, setEditClient] = useState<Partial<Client> | null>(null)
  const notes = useQuery({
    queryKey: ['client-notes', id, notePage],
    queryFn: async () => {
      const { data, error, count } = await supabase.from('contact_activity')
        .select('id, body, type, created_at', { count: 'exact' })
        .eq('contact_id', id).order('created_at', { ascending: false })
        .range(notePage * PAGE, notePage * PAGE + PAGE - 1)
      if (error) throw error
      return { rows: (data ?? []) as NoteRow[], count: count ?? 0 }
    },
  })
  const refreshNotes = () => qc.invalidateQueries({ queryKey: ['client-notes', id] })

  const addNote = async () => {
    if (!newNote.trim()) return
    const { error } = await supabase.from('contact_activity').insert({ company_id: companyId, contact_id: id, user_id: user?.id ?? null, type: 'note', body: newNote.trim() })
    if (error) return toast.error(error.message)
    setNewNote(''); setNotePage(0); refreshNotes()
  }
  const saveEdit = async () => {
    if (!editing) return
    // contact_activity has no updated_at column, and PostgREST rejects the whole request for an
    // unknown key (PGRST204). Editing a client note therefore failed every time with a red
    // toast and no note saved. Found 2026-09-18 while answering a question about notes.
    const { error } = await supabase.from('contact_activity').update({ body: editText.trim() }).eq('id', editing.id)
    if (error) return toast.error(error.message)
    setEditing(null); refreshNotes()
  }
  const deleteNote = async (n: NoteRow) => {
    if (!confirm('Delete this note?')) return
    const { error } = await supabase.from('contact_activity').delete().eq('id', n.id)
    if (error) return toast.error(error.message)
    refreshNotes()
  }

  if (isLoadingDetail) return <Page><div className="h-6 w-40 animate-pulse rounded bg-canvas-subtle" /></Page>
  if (!client) {
    return (
      <Page>
        <PageHeader title="Client not found" />
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/clients' })}><ArrowLeft className="h-4 w-4" /> Back to clients</button>
      </Page>
    )
  }

  const isActive = client.status !== 'inactive' && client.status !== 'archived'
  // Same trap the job page hit: RouteMate-era clients keep the street in line1 with `address`
  // empty, so listing `address` first dropped the street and showed suburb + postcode only.
  const addr = composeAddress({
    line1: client.line1 ?? '', line2: client.line2 ?? '', city: client.city ?? '',
    state: client.state ?? '', postal_code: client.postal_code ?? '', address: client.address ?? '',
  })
  const toggleStatus = async () => {
    try { await update.mutateAsync({ id: client.id, status: isActive ? 'inactive' : 'active' }); toast.success(isActive ? 'Marked inactive' : 'Marked active') }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }
  const delClient = async () => {
    if (!confirm('Delete this client?')) return
    try { await remove.mutateAsync(client.id); toast.success('Client deleted'); navigate({ to: '/clients' }) }
    catch (e) { toast.error(e instanceof Error ? e.message : 'Failed') }
  }

  const jobRows = jobs.data?.rows ?? []

  return (
    <Page>
      <button onClick={() => navigate({ to: '/clients' })} className="mb-3 inline-flex items-center gap-1 text-[13px] font-medium text-token-muted transition-colors hover:text-token-secondary">
        <ArrowLeft className="h-3.5 w-3.5" /> All clients
      </button>
      <PageHeader title={clientName(client)} actions={<>
        <button className={btnSecondary} onClick={toggleStatus}>{isActive ? 'Mark inactive' : 'Mark active'}</button>
        <button className={btnSecondary} onClick={delClient}><Trash2 className="h-4 w-4" /></button>
      </>} />
      <div className="mt-3"><StatusPill status={isActive ? 'active' : client.status || 'inactive'} /></div>

      {/* Job history */}
      <div className="mt-6">
        <ClientAssetsCard contactId={id} />

        <Card className="mt-5">
          <CardHeader title="Job history" count={jobs.data?.count} action={
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <input value={jobSearch} onChange={(e) => { setJobSearch(e.target.value); setJobPage(0) }} placeholder="Search jobs…"
                className="h-9 w-full rounded-[10px] border border-token-subtle px-3 text-[13px] outline-none focus:border-token-strong sm:w-48" />
              <button className={btnPrimary} onClick={() => navigate({ to: '/jobs/new', search: { contact: id } })}><Plus className="h-4 w-4" /> New job</button>
            </div>
          } />
          {jobs.isLoading ? <SkeletonRows count={3} /> : jobRows.length === 0 ? (
            <EmptyState label={jobSearch ? 'No matches.' : 'No jobs for this client yet.'} cta={jobSearch ? undefined : 'New job'} onCta={() => navigate({ to: '/jobs/new', search: { contact: id } })} />
          ) : (
            <>
              <Table className="-mx-px rounded-none border-x-0 border-b-0 border-t border-token-subtle shadow-none" cols={JOB_COLS} header={<><span>Job</span><span>Assigned</span><span>Scheduled</span><span className="text-right">Status</span></>}>
                {jobRows.map((j, i) => (
                  <Trow key={j.id} cols={JOB_COLS} last={i === jobRows.length - 1} onClick={() => navigate({ to: '/jobs/$id', params: { id: String(j.id) } })}>
                    <span className="min-w-0">
                      <span className="block truncate font-bold text-token-primary">{j.name || 'Untitled job'}</span>
                      {j.address && <span className="block truncate text-[12px] text-token-secondary">{j.address}</span>}
                    </span>
                    <span className="truncate text-token-secondary">{userName(j.user_id)}</span>
                    <span className="text-token-secondary">{j.planned_start_date ? fmt(j.planned_start_date) : '—'}</span>
                    <span className="flex items-center justify-end gap-1.5">
                      <button title="Duplicate as new job" onClick={(e) => { e.stopPropagation(); dupJob(j.id) }}
                        className="rounded-md p-1 text-token-muted transition hover:bg-canvas-subtle hover:text-token-primary">
                        <Copy className="h-3.5 w-3.5" />
                      </button>
                      <StatusPill status={j.status || 'pending'} />
                    </span>
                  </Trow>
                ))}
              </Table>
              <Pagination page={jobPage} pageSize={PAGE} total={jobs.data?.count ?? 0} onPage={setJobPage} />
            </>
          )}
        </Card>
      </div>

      {/* Communications */}
      <div className="mt-5">
        <Card>
          <CardHeader title="Communications" count={emails.data?.count} />
          {emails.isLoading ? <SkeletonRows count={2} /> : (emails.data?.count ?? 0) === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-token-secondary">No emails sent to this client yet. Scheduled emails are sent by the notification worker and logged here.</p>
          ) : (
            <>
              <ul className="divide-y divide-token-subtle">
                {emails.data!.rows.map((e) => (
                  <li key={e.id}>
                    <button onClick={() => setOpenEmail(e)} className="flex w-full items-center justify-between gap-3 px-5 py-3 text-left transition-colors hover:bg-token-surface">
                      <span className="flex min-w-0 items-center gap-3">
                        <span className={`flex h-9 w-9 flex-none items-center justify-center rounded-[9px] ${e.success ? 'bg-token-success-surface text-token-success' : 'bg-token-danger-surface text-token-danger'}`}><Mail className="h-4 w-4" /></span>
                        <span className="min-w-0">
                          <span className="block truncate text-[14px] font-semibold text-token-primary">{e.subject || '(no subject)'}</span>
                          <span className="block truncate text-[12px] text-token-secondary">to {e.to_addresses || '—'}</span>
                        </span>
                      </span>
                      <span className="flex flex-none items-center gap-2.5">
                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold ${e.success ? 'bg-token-success-surface text-token-success' : 'bg-token-danger-surface text-token-danger'}`}>
                          {e.success ? <Check className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}{e.success ? 'Sent' : 'Failed'}
                        </span>
                        <span className="text-[12px] tabular-nums text-token-secondary">{fmt(e.created_at)}</span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
              <Pagination page={commPage} pageSize={PAGE} total={emails.data?.count ?? 0} onPage={setCommPage} />
            </>
          )}
        </Card>
      </div>

      {/* Messages */}
      <div className="mt-5"><MessageThread contactId={id} /></div>

      {/* Documents */}
      <div className="mt-5"><ClientDocuments contactId={id} /></div>

      <div className="mt-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
        <Card>
          <CardHeader title="Details" action={
            <button className={btnGhost} onClick={() => setEditClient({
              first_name: client.first_name, last_name: client.last_name, email: client.email,
              phone: client.phone, mobile: client.mobile, organization: client.organization,
              address: client.address, city: client.city, state: client.state, postal_code: client.postal_code,
            })}><Pencil className="h-3.5 w-3.5" /> Edit</button>
          } />
          <div className="px-5 py-3">
            <KV k="Email" v={client.email} />
            <KV k="Phone" v={client.phone ? displayPhMobile(client.phone) : client.phone} />
            <KV k="Mobile" v={client.mobile ? displayPhMobile(client.mobile) : client.mobile} />
            <KV k="Business" v={client.organization} />
            <KV k="Address" v={addr} />
          </div>
        </Card>

        {/* Notes (add / edit / delete + paginated) */}
        <Card>
          <CardHeader title="Notes" count={notes.data?.count} />
          <div className="border-b border-token-subtle px-5 py-3">
            <div className="flex gap-2">
              <Input value={newNote} onChange={(e) => setNewNote(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addNote()} placeholder="Add a note…" />
              <button className={btnPrimary} onClick={addNote} disabled={!newNote.trim()}>Add</button>
            </div>
          </div>
          {notes.isLoading ? <SkeletonRows count={3} /> : (notes.data?.count ?? 0) === 0 ? (
            <p className="px-5 py-8 text-center text-[13px] text-token-secondary">No notes yet.</p>
          ) : (
            <>
              <ul className="divide-y divide-token-subtle">
                {notes.data!.rows.map((n) => (
                  <li key={n.id} className="group px-5 py-3">
                    <div className="flex items-start justify-between gap-3">
                      <p className="whitespace-pre-wrap text-[13.5px] text-token-primary">{n.body || n.type}</p>
                      <span className={cn('flex flex-none items-center gap-1', hoverActions)}>
                        <button className={btnGhost} onClick={() => { setEditing(n); setEditText(n.body ?? '') }}><Pencil className="h-3.5 w-3.5" /></button>
                        <button className={btnGhost} onClick={() => deleteNote(n)}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></button>
                      </span>
                    </div>
                    <p className="mt-0.5 text-[11.5px] tabular-nums text-token-muted">{fmt(n.created_at)}</p>
                  </li>
                ))}
              </ul>
              <Pagination page={notePage} pageSize={PAGE} total={notes.data?.count ?? 0} onPage={setNotePage} />
            </>
          )}
        </Card>
      </div>

      {/* Email viewer */}
      <Modal open={!!openEmail} onClose={() => setOpenEmail(null)} title={openEmail?.subject || 'Email'} wide>
        {openEmail && (
          <div className="space-y-3">
            <div className="flex items-center justify-between text-[12.5px] text-token-secondary">
              <span>to {openEmail.to_addresses || '—'}</span>
              <span className="tabular-nums">{fmt(openEmail.created_at)}</span>
            </div>
            {!openEmail.success && openEmail.mailer_response && (
              <p className="rounded-[8px] bg-token-danger-surface px-3 py-2 text-[12.5px] text-token-danger">Delivery failed: {openEmail.mailer_response}</p>
            )}
            <div className="max-h-[55vh] overflow-y-auto rounded-[10px] border border-token-subtle bg-token-surface p-4 text-[13.5px] leading-relaxed text-token-primary [&_a]:text-token-primary"
              dangerouslySetInnerHTML={{ __html: openEmail.body || '<p>No content.</p>' }} />
          </div>
        )}
      </Modal>

      {/* Edit client details */}
      <Modal open={!!editClient} onClose={() => setEditClient(null)} title="Edit client"
        footer={<><button className={btnSecondary} onClick={() => setEditClient(null)}><X className="h-4 w-4" /> Cancel</button>
          <button className={btnPrimary} disabled={update.isPending} onClick={async () => {
            if (!editClient) return
            try { await update.mutateAsync({ id: client.id, ...editClient }); toast.success('Client updated'); setEditClient(null) }
            catch (e) { toast.error(e instanceof Error ? e.message : 'Could not update') }
          }}>{update.isPending ? 'Saving…' : 'Save'}</button></>}>
        {editClient && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <Field label="First name"><Input value={editClient.first_name ?? ''} onChange={(e) => setEditClient({ ...editClient, first_name: e.target.value })} /></Field>
            <Field label="Last name"><Input value={editClient.last_name ?? ''} onChange={(e) => setEditClient({ ...editClient, last_name: e.target.value })} /></Field>
            <Field label="Email" className="sm:col-span-2"><Input type="email" value={editClient.email ?? ''} onChange={(e) => setEditClient({ ...editClient, email: e.target.value })} /></Field>
            <Field label="Phone"><Input value={editClient.phone ?? ''} onChange={(e) => setEditClient({ ...editClient, phone: e.target.value })} /></Field>
            <Field label="Mobile"><Input value={editClient.mobile ?? ''} onChange={(e) => setEditClient({ ...editClient, mobile: e.target.value })} /></Field>
            <Field label="Business" className="sm:col-span-2"><Input value={editClient.organization ?? ''} onChange={(e) => setEditClient({ ...editClient, organization: e.target.value })} /></Field>
            <AddressPicker
              className="sm:col-span-2"
              value={{
                address: editClient.address ?? null, line1: editClient.line1 ?? null, line2: editClient.line2 ?? null,
                city: editClient.city ?? null, state: editClient.state ?? null, postal_code: editClient.postal_code ?? null,
                manual_address: editClient.manual_address ?? false,
              }}
              onChange={(a: AddressValue) => setEditClient({
                ...editClient, address: a.address, line1: a.line1, line2: a.line2,
                city: a.city, state: a.state, postal_code: a.postal_code,
                manual_address: a.manual_address,
                latitude: a.latitude ?? editClient.latitude ?? null,
                longitude: a.longitude ?? editClient.longitude ?? null,
              })}
            />
          </div>
        )}
      </Modal>

      {/* Edit note */}
      <Modal open={!!editing} onClose={() => setEditing(null)} title="Edit note"
        footer={<><button className={btnSecondary} onClick={() => setEditing(null)}><X className="h-4 w-4" /> Cancel</button><button className={btnPrimary} onClick={saveEdit} disabled={!editText.trim()}>Save</button></>}>
        <Textarea rows={5} value={editText} onChange={(e) => setEditText(e.target.value)} autoFocus />
      </Modal>
    </Page>
  )
}
